// Prod_US Details sheet (spec: Prod_US_Report_Changes.md section 5): the person's profile as
// stacked rows, with Edit for initials, birth month and year, height and sex. The IDs are
// read-only. Replaces the staff edit page, which asks for email, phone and a required weight
// that this product does not collect.
import React, { useEffect, useState } from "react";
import { getProdUsMe, updateProdUsPerson } from "./prodUsApi";
import ProdUsAboutFields from "./ProdUsAboutFields";
import { storeProdUsPerson } from "./prodUsSession";
import { aboutErrors, heightCm } from "./prodUsRules";
import { MONTHS, pt } from "./prodUsStrings";

const CM_PER_INCH = 2.54;

const isFemale = (person) => /^f/i.test(person?.gender || "");

const heightParts = (cm) => {
  const inches = Math.round(Number(cm) / CM_PER_INCH);
  return { feet: Math.floor(inches / 12), inches: inches % 12 };
};

const toDraft = (person) => {
  const height = person.height_cm ? heightParts(person.height_cm) : null;
  return {
    fi: person.first_name || "",
    li: person.last_name || "",
    birthMonth: person.birth_month ? String(person.birth_month) : "",
    birthYear: person.birth_year ? String(person.birth_year) : "",
    feet: height ? String(height.feet) : "",
    inches: height ? String(height.inches) : "",
    sex: isFemale(person) ? "F" : "M",
  };
};

const toFields = (draft) => ({
  first_name: draft.fi,
  last_name: draft.li,
  gender: draft.sex === "F" ? "female" : "male",
  birth_year: Number(draft.birthYear),
  birth_month: Number(draft.birthMonth),
  height: heightCm(draft.feet, draft.inches),
});

const addedOn = (value) => {
  const date = value ? new Date(value) : null;
  return date && !Number.isNaN(date.getTime())
    ? date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : "";
};

const Row = ({ label, value }) => (
  <div className="det-row">
    <span>{label}</span>
    <b className="notranslate">{value}</b>
  </div>
);

const ProdUsDetails = ({ open, onClose, onSaved }) => {
  const [person, setPerson] = useState(null);
  const [draft, setDraft] = useState(null); // set while editing
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const [saveErr, setSaveErr] = useState("");

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setDraft(null);
    setTried(false);
    setSaveErr("");
    // Fresh from the backend: the app's stored row has no height or birth fields after a reload.
    getProdUsMe()
      .then((me) => {
        if (!cancelled) setPerson(me?.person || null);
      })
      .catch(() => {
        if (!cancelled) setPerson(null);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  if (!open) return null;

  const errors = draft && tried ? aboutErrors(draft) : {};

  const save = async () => {
    setTried(true);
    if (Object.keys(aboutErrors(draft)).length) return;
    setBusy(true);
    setSaveErr("");
    try {
      await updateProdUsPerson(person.id, toFields(draft));
      const me = await getProdUsMe();
      setPerson(me.person);
      storeProdUsPerson(me.person);
      setDraft(null);
      onSaved?.(me.person);
    } catch (error) {
      setSaveErr(error?.response?.data?.message || pt("save_failed"));
    } finally {
      setBusy(false);
    }
  };

  const sexWord = pt(isFemale(person) ? "female" : "male");
  const height = person?.height_cm ? heightParts(person.height_cm) : null;
  const added = addedOn(person?.createdAt);

  return (
    <div className="produs pu-details" role="presentation">
      <div
        className="det-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="pu-details-title"
      >
        <div className="det-top">
          <div>
            <h2 id="pu-details-title" className="notranslate">
              {`${person?.first_name || ""}${person?.last_name || ""}`}
            </h2>
            {person ? (
              <p>
                {`${sexWord} | ${person.age}`}
                {added ? ` · ${pt("det_added", { date: added })}` : ""}
              </p>
            ) : null}
          </div>
          <button
            type="button"
            className="ct-x"
            onClick={onClose}
            aria-label={pt("close")}
          >
            ×
          </button>
        </div>
        {person ? (
          <div className="det-body">
            <section className={`det-sec${draft ? " editing" : ""}`}>
              <div className="det-head">
                <h3>{pt("det_sec")}</h3>
                {draft ? null : (
                  <button
                    type="button"
                    className="det-edit"
                    onClick={() => setDraft(toDraft(person))}
                  >
                    {pt("det_edit")}
                  </button>
                )}
              </div>
              <Row label={pt("det_client_id")} value={person.test_person_id} />
              <Row label={pt("det_sl_id")} value={person.seniorlife_id} />
              {draft ? (
                <>
                  <div className="det-form">
                    <ProdUsAboutFields
                      about={draft}
                      setAbout={setDraft}
                      errors={errors}
                    />
                  </div>
                  {saveErr ? (
                    <p className="aerr" role="alert">
                      {saveErr}
                    </p>
                  ) : null}
                  <div className="det-actions">
                    <button
                      type="button"
                      className="det-save"
                      disabled={busy}
                      onClick={save}
                    >
                      {pt("det_save")}
                    </button>
                    <button
                      type="button"
                      className="det-cancel"
                      disabled={busy}
                      onClick={() => setDraft(null)}
                    >
                      {pt("cancel")}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <Row label={pt("sex")} value={sexWord} />
                  <Row
                    label={pt("det_born")}
                    value={
                      person.birth_month
                        ? `${MONTHS[person.birth_month - 1]} ${person.birth_year}`
                        : ""
                    }
                  />
                  <Row
                    label={pt("height")}
                    value={
                      height
                        ? `${height.feet} ${pt("ft")} ${height.inches} ${pt("in")}`
                        : ""
                    }
                  />
                </>
              )}
            </section>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default ProdUsDetails;
